import { APIURL, apiGet, cancelSelectEvent, getCSSStyles, getWidgetDataURI, toggleWidgetTheme, normalizeWidgetTheme, getEChartsTooltipTheme, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

const TOP_N_OPTIONS = [5, 10, 15, 20];

const EXCHANGE_COLORS = [
    '#8B6CFF', '#6FF19A', '#FF2B2B', '#FFA01C', '#44CA6C',
    '#3B82F6', '#EC4899', '#14B8A6', '#F59E0B', '#6366F1'
];

interface ExchangeAcquisitionSummary {
    total_acquisitions: number;
    exchanges_count: number;
    top_exchange: string;
    top_exchange_count: number;
    top_exchange_percentage: number;
}

interface ExchangeAcquisitionItem {
    exchange: string;
    acquisitions_count: number;
    percentage: number;
    percentage_display?: string;
}

interface ExchangeAcquisitionsApiData {
    summary: ExchangeAcquisitionSummary;
    pie_data: ExchangeAcquisitionItem[];
    detailed_breakdown: ExchangeAcquisitionItem[];
}

interface ExchangeChartItem {
    name: string;
    value: number;
    count: number;
    percentage: number;
    percentageDisplay: string;
}

export class TraderversePercentageofAcquisitionsperExchange {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private currentData: ExchangeChartItem[] = [];
    private apiData: ExchangeAcquisitionsApiData | null = null;
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private outsideClickHandler: ((ev: MouseEvent) => void) | null = null;
    private filters = {
        topN: 10
    };

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.filters.topN = Number((config as any).topN ?? (this.config as any).topN ?? this.filters.topN);
        (this.config as any).topN = this.filters.topN;
        this.container = this.resolveContainer(config.container);
        this.shadowRoot = (this.container as any).shadowRoot || this.container.attachShadow({ mode: 'closed' });
        this.init();
        this.widgetResizeObserver = new ResizeObserver(() => this.updateDisplay());
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        return {
            headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            topN: 10
        } as any;
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private renderTopNOptions(): string {
        const options = TOP_N_OPTIONS.includes(this.filters.topN)
            ? TOP_N_OPTIONS
            : [...TOP_N_OPTIONS, this.filters.topN].sort((a, b) => a - b);
        return options
            .map(n => `<option value="${n}"${n === this.filters.topN ? ' selected' : ''}>${n}</option>`)
            .join('');
    }

    private filtersIconSvg(): string {
        return `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg>`;
    }

    private async init(): Promise<void> {
        await this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart();
        if (widget) widget.classList.remove('loading');
        cancelSelectEvent(this.shadowRoot);
        this.emit('ready', {});
        this.config.onReady?.();
    }

    private async createWidget(): Promise<void> {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
            <div class="headTop">
                <h4 class="title market">${this.getTitleIconSvg()} Percentage of Acquisitions per Exchange</h4>
                <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
                    <div class="item">
                        <button class="filters-toggle" type="button" aria-label="Toggle filters">${this.filtersIconSvg()}</button>
                    </div>
                    <i class="pi pi-ellipsis-v"></i>
                </div>
            </div>
            <div class="inBody">
                <div class="optionsRow">
                    <span></span>
                    <div class="filtersBox sideBar hidden">
                        <div class="item">
                            <label>Top N</label>
                            <select class="topn-select" aria-label="Top N" data-testing-id="topn-select">${this.renderTopNOptions()}</select>
                        </div>
                    </div>
                </div>
                <div class="chartDv cryptoCompany" id="exchangeAcquisitionsContainer"></div>
            </div>
        </div>`;
        this.shadowRoot.innerHTML = widgetHtml;
        await this.attachStyles();
        setupHeaderPin(this.shadowRoot, this.config);
        this.wireFilterControls();
    }

    private wireFilterControls(): void {
        const filterBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement | null;
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
        const topNSelect = this.shadowRoot.querySelector('.topn-select') as HTMLSelectElement | null;

        if (filterBtn && filtersBox) {
            filterBtn.addEventListener('click', (ev: MouseEvent) => {
                ev.stopPropagation();
                filtersBox.classList.toggle('hidden');
            });
            filtersBox.addEventListener('click', (ev: MouseEvent) => ev.stopPropagation());

            this.outsideClickHandler = (ev: MouseEvent) => {
                const anyEv: any = ev;
                const path: EventTarget[] = (anyEv.composedPath && anyEv.composedPath()) || anyEv.path || [];
                const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;
                const clickedInside = path.indexOf(this.container) !== -1 || (widgetBox && path.indexOf(widgetBox) !== -1);
                if (!clickedInside && !filtersBox.classList.contains('hidden')) {
                    filtersBox.classList.add('hidden');
                }
            };
            document.addEventListener('click', this.outsideClickHandler);
        }

        const applyFiltersAndFetch = () => {
            this.filters.topN = Number(topNSelect?.value || this.filters.topN);
            (this.config as any).topN = this.filters.topN;
            this.setupChart();
        };

        topNSelect?.addEventListener('change', applyFiltersAndFetch);
    }

    private buildQueryParams(): Record<string, string | number> {
        return {
            topN: this.filters.topN,
            limit: this.filters.topN
        };
    }

    private mapApiData(data: ExchangeAcquisitionsApiData): ExchangeChartItem[] {
        const rows = (data.detailed_breakdown?.length ? data.detailed_breakdown : data.pie_data) ?? [];
        return rows
            .map((item) => ({
                name: item.exchange,
                value: Number(item.percentage) || 0,
                count: Number(item.acquisitions_count) || 0,
                percentage: Number(item.percentage) || 0,
                percentageDisplay: item.percentage_display || `${Number(item.percentage || 0).toFixed(2)}%`
            }))
            .filter((item) => item.count > 0 || item.percentage > 0)
            .slice(0, this.filters.topN || 10);
    }

    private hasChartData(data: ExchangeAcquisitionsApiData | null): boolean {
        if (!data) return false;
        const rows = data.pie_data ?? data.detailed_breakdown ?? [];
        return rows.some((item) => (item.acquisitions_count ?? 0) > 0 || (item.percentage ?? 0) > 0);
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
            script.async = true;
            script.onload = () => resolve(true);
            this.shadowRoot.appendChild(script);
        });
    }

    private async setupChart(): Promise<void> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const container = this.shadowRoot.querySelector('#exchangeAcquisitionsContainer');
        if (widget) widget.classList.add('loading');

        try {
            const res = await apiGet({
                method: 'GET',
                url: `${APIURL}/v1/mergers-aquisitions/exchange-acquisitions`,
                query: this.buildQueryParams(),
                headers: { }
            });
            if (!res.ok) throw new Error('Failed to fetch exchange acquisitions data');

            const json = await res.json();
            if (json && typeof json.isSuccess === 'boolean' && !json.isSuccess) {
                throw new Error(json.message || 'API returned isSuccess: false');
            }

            const responseData = (json?.data ?? null) as ExchangeAcquisitionsApiData | null;
            this.apiData = responseData;

            if (this.hasChartData(this.apiData)) {
                container?.setAttribute('data-testing-id', 'percentage-acquisitions-per-exchange-chart');
            } else {
                container?.removeAttribute('data-testing-id');
            }

            if (!this.hasChartData(this.apiData)) {
                this.currentData = [];
                if (container) container.innerHTML = '<div class="noData"><p>No data available</p></div>';
                if (this.currentChart) {
                    this.currentChart.dispose();
                    this.currentChart = null;
                }
            } else {
                this.currentData = this.mapApiData(this.apiData!);
                this.renderChart();
            }

            this.emit('dataUpdate', this.apiData);
        } catch (err) {
            console.error('Failed to fetch/setup chart data:', err);
            this.currentData = [];
            this.apiData = null;
            if (container) container.innerHTML = '<div class="noData"><p>Failed to load data</p></div>';
            this.emit('error', err);
        } finally {
            if (widget) widget.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        if (!this.currentData?.length || !(window as any).echarts) {
            return;
        }
        this.renderChart();
    }

    private renderChart(): void {
        const container = this.shadowRoot.querySelector('#exchangeAcquisitionsContainer');
        if (!container) return;

        container.innerHTML = `<div id="exchangeAcquisitionsChart" class="chart coverDv"></div>`;
        this.drawChart();
    }

    private truncateLabel(label: string, maxLen = 16): string {
        if (!label) return '';
        return label.length > maxLen ? `${label.slice(0, maxLen - 1)}…` : label;
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#exchangeAcquisitionsChart') as HTMLElement;
        if (!container || !this.currentData?.length) return;

        if (this.currentChart) {
            this.currentChart.dispose();
        }

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm');
        const bgColor = getComputedStyle(widgetBox).getPropertyValue('--base-dark');
        const summary = this.apiData?.summary;

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'item',
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (p: any) => {
                    return `
                  <div style="min-width:160px">
                    <div style="font-weight:600;margin-bottom:6px">
                      <span style="
                        display:inline-block;
                        width:10px;
                        height:10px;
                        border-radius:50%;
                        background:${p.color};
                        margin-right:6px
                      "></span>
                      ${p.name}
                    </div>
                    <div>Acquisitions <b>${p.data.count.toLocaleString()}</b></div>
                    <div>${p.data.percentageDisplay || `${Number(p.value).toFixed(2)}%`} of total</div>
                    ${summary?.total_acquisitions != null
                        ? `<div style="margin-top:6px;opacity:.8">Total acquisitions: <b>${summary.total_acquisitions.toLocaleString()}</b></div>`
                        : ''}
                  </div>
                `;
                }
            },
            series: [
                {
                    type: 'pie',
                    radius: ['30%', '90%'],
                    center: ['50%', '55%'],
                    startAngle: 90,
                    minAngle: 6,
                    avoidLabelOverlap: true,
                    padAngle: 3,
                    itemStyle: {
                        borderRadius: 8,
                        borderColor: bgColor,
                        borderWidth: 6
                    },
                    label: {
                        show: true,
                        position: 'inside',
                        formatter: (p: any) => `${this.truncateLabel(p.name)}\n${Number(p.value).toFixed(1)}%`,
                        color: '#FFFFFF',
                        fontSize: fontSize,
                        textBorderColor: "#000000",
                        textBorderWidth: 2,
                        textBorderType: 'solid',
                    },
                    labelLine: { show: false },
                    data: this.currentData.map((d, index) => ({
                        ...d,
                        itemStyle: { color: EXCHANGE_COLORS[index % EXCHANGE_COLORS.length] }
                    }))
                }
            ]
        };

        this.currentChart = echarts.init(container);
        this.currentChart.setOption(option);
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

        this.updateDisplay();
    }

    public destroy(): void {
        if (this.currentChart) {
            this.currentChart.dispose();
            this.currentChart = null;
        }
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
            this.widgetResizeObserver = null;
        }
        if (this.outsideClickHandler) {
            document.removeEventListener('click', this.outsideClickHandler);
            this.outsideClickHandler = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach(h => h(evt));
        }
    }

    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }
}

declare global { interface Window { TraderversePercentageofAcquisitionsperExchange: typeof TraderversePercentageofAcquisitionsperExchange; } }
if (typeof window !== 'undefined') window.TraderversePercentageofAcquisitionsperExchange = TraderversePercentageofAcquisitionsperExchange;
